/**
 * Product-page additions for Location Service Engine tenants: brand and SKU (``product_brand_sku``) and the
 * Phase 2 "Frequently bought together" rail (``recommendations`` — products from this tenant's own orders,
 * priced by the server). Renders nothing for any other tenant (no API call either).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Tag } from "lucide-react";
import { api } from "../../lib/api";
import { inr, useLse } from "../../lib/lse";
import { useAuth } from "../../contexts/AuthContext";

export function LseBrandSku({ product }) {
    const { has } = useLse();
    if (!has("product_brand_sku") || !(product?.brand || product?.sku)) return null;
    return (
        <p className="mt-1 text-xs text-muted-foreground inline-flex items-center gap-1.5 flex-wrap" data-testid="lse-brand-sku">
            <Tag size={12} />
            {product.brand && <span>Brand: <span className="font-semibold text-foreground">{product.brand}</span></span>}
            {product.brand && product.sku && <span aria-hidden>·</span>}
            {product.sku && <span>SKU: <span className="font-mono">{product.sku}</span></span>}
        </p>
    );
}

export function LseBoughtTogether({ productId }) {
    const { has } = useLse();
    const { user } = useAuth();
    const on = has("recommendations") && !!user && !!productId;
    const [rows, setRows] = useState(null);
    useEffect(() => {
        if (!on) return;
        api.get(`/lse/recommendations/together?product_id=${encodeURIComponent(productId)}`)
            .then(({ data }) => setRows(data.products || [])).catch(() => setRows([]));
    }, [on, productId]);
    if (!on || !rows?.length) return null;
    return (
        <section className="mt-5" data-testid="lse-bought-together">
            <h2 className="text-sm font-bold inline-flex items-center gap-1.5"><Sparkles size={15} className="text-primary" /> Frequently bought together</h2>
            <div className="mt-2 flex gap-3 overflow-x-auto pb-1 no-scrollbar">
                {rows.map((p) => (
                    <Link key={p.id} to={`/product/${p.id}`} className="shrink-0 w-[120px] rounded-2xl border border-border bg-card p-2" data-testid={`lse-together-${p.id}`}>
                        <div className="aspect-square rounded-xl bg-muted overflow-hidden">
                            {p.images?.[0] && <img src={p.images[0]} alt="" loading="lazy" className="w-full h-full object-cover" />}
                        </div>
                        <p className="mt-1.5 text-xs font-semibold line-clamp-2 min-h-[2rem]">{p.name}</p>
                        <p className="text-xs font-bold">{inr(p.selling_price)}</p>
                    </Link>
                ))}
            </div>
        </section>
    );
}
